import React, { useEffect, useMemo, useState } from 'react';
import { Table, Form } from 'react-bootstrap';
import Select from 'react-select';
import { postRequestOptions, getRequestOptions } from '../helpers/Fetchwrapper';
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { formatReportQuantity, numberFormat } from '../helpers/GlobalHelper';
import { useLocation } from 'react-router-dom';

interface ProductCategory {
    id_product_categories: number;
    category_name: string;
    category_code: string;
}

interface CategoryValue {
    quantity: number;
    amount: number;
}

interface MonthlyRow {
    month: string;
    categories: { [categoryId: string]: CategoryValue };
    total: CategoryValue;
}

interface GradeWiseSalesData {
    categories: ProductCategory[];
    months: MonthlyRow[];
    grand_totals: {
        categories: { [categoryId: string]: CategoryValue };
        total: CategoryValue;
    };
    period_label: string;
}

const getDefaultMonthRange = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const currentMonth = `${now.getFullYear()}-${month}`;

    return {
        fromDate: currentMonth,
        toDate: currentMonth,
    };
};

const GradeWiseSalesReport: React.FC = () => {
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const defaultRange = getDefaultMonthRange();

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState<GradeWiseSalesData | null>(null);
    const [projects, setProjects] = useState<{ label: string; value: string }[]>([]);
    const [project, setProject] = useState({ label: 'Select Project', value: '' });
    const [formData, setFormData] = useState({
        idProjects: query.get('idProjects') ?? '',
        fromDate: query.get('fromDate') ?? defaultRange.fromDate,
        toDate: query.get('toDate') ?? defaultRange.toDate,
        showAmount: query.get('showAmount') === 'true',
        includeFreeItem: query.get('includeFreeItem') === 'true',
    });

    const visibleCategories = useMemo(() => {
        if (!reportData) {
            return [];
        }

        return reportData.categories.filter((category) => {
            const categoryKey = String(category.id_product_categories);
            const grandTotal = reportData.grand_totals.categories[categoryKey] ?? { quantity: 0, amount: 0 };
            if (grandTotal.quantity !== 0 || grandTotal.amount !== 0) {
                return true;
            }

            return reportData.months.some((row) => {
                const cell = row.categories[categoryKey] ?? { quantity: 0, amount: 0 };
                return cell.quantity !== 0 || cell.amount !== 0;
            });
        });
    }, [reportData]);

    useEffect(() => {
        getProjects();
    }, []);

    useEffect(() => {
        if (projects.length > 0) {
            const projectObj = Number(query.get('idProjects'))
                ? projects.find((item) => item.value === Number(query.get('idProjects')))
                : projects[0];

            setProject({
                label: projectObj?.label ?? 'Select Project',
                value: projectObj?.value ?? '',
            });
            setFormData((prev) => ({
                ...prev,
                idProjects: projectObj ? String(projectObj.value) : '',
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects]);

    const getProjects = () => {
        fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const projectOptions = resp.data.map((item: any) => ({
                    label: item.name,
                    value: item.id,
                }));
                setProjects(projectOptions);
            })
            .catch((error) => console.error('Error fetching projects:', error));
    };

    const handleProjectChange = (selected: any) => {
        setProject(selected);
        setFormData((prev) => ({
            ...prev,
            idProjects: selected ? String(selected.value) : '',
        }));
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleViewReport = async () => {
        if (!formData.idProjects) {
            toast.error('Please select a project', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (!formData.fromDate || !formData.toDate) {
            toast.error('Please select both From Month and To Month', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (formData.fromDate > formData.toDate) {
            toast.error('To Month must be after or equal to From Month', { position: 'bottom-right', autoClose: 4000 });
            return;
        }

        setLoading(true);
        try {
            const { showAmount, ...requestData } = formData;
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'mis-reports/grade-wise-sales-report',
                postRequestOptions(requestData)
            );
            const data = await response.json();
            if (data.success) {
                setReportData(data.data);
            } else {
                setReportData(null);
                toast.error(data.message ?? 'Failed to load report', { position: 'bottom-right', autoClose: 4000 });
            }
        } catch (error) {
            console.error('Error submitting data:', error);
            toast.error('Failed to load report', { position: 'bottom-right', autoClose: 4000 });
        } finally {
            setLoading(false);
        }
    };

    const getCategoryLabel = (category: ProductCategory) =>
        category.category_code || category.category_name;

    const getCategoryValue = (value: CategoryValue | undefined) =>
        value ?? { quantity: 0, amount: 0 };

    const renderQuantityTable = () => {
        if (!reportData) {
            return null;
        }

        return (
            <Table responsive striped bordered hover size="sm">
                <thead>
                    <tr>
                        <th>Month</th>
                        {visibleCategories.map((category) => (
                            <th key={category.id_product_categories} className="text-end">
                                {getCategoryLabel(category)}
                            </th>
                        ))}
                        <th className="text-end">Total</th>
                    </tr>
                </thead>
                <tbody>
                    {reportData.months.map((row) => (
                        <tr key={row.month}>
                            <td>{row.month}</td>
                            {visibleCategories.map((category) => {
                                const categoryData = getCategoryValue(row.categories[String(category.id_product_categories)]);
                                return (
                                    <td key={category.id_product_categories} className="text-end">
                                        {formatReportQuantity(categoryData.quantity)}
                                    </td>
                                );
                            })}
                            <td className="text-end">{formatReportQuantity(row.total.quantity)}</td>
                        </tr>
                    ))}
                    <tr>
                        <td><strong>Grand Total</strong></td>
                        {visibleCategories.map((category) => {
                            const categoryData = getCategoryValue(reportData.grand_totals.categories[String(category.id_product_categories)]);
                            return (
                                <td key={category.id_product_categories} className="text-end">
                                    <strong>{formatReportQuantity(categoryData.quantity)}</strong>
                                </td>
                            );
                        })}
                        <td className="text-end">
                            <strong>{formatReportQuantity(reportData.grand_totals.total.quantity)}</strong>
                        </td>
                    </tr>
                </tbody>
            </Table>
        );
    };

    const renderQuantityAmountTable = () => {
        if (!reportData) {
            return null;
        }

        return (
            <Table responsive striped bordered hover size="sm">
                <thead>
                    <tr>
                        <th rowSpan={2}>Month</th>
                        {visibleCategories.map((category) => (
                            <th key={category.id_product_categories} colSpan={2} className="text-center">
                                {getCategoryLabel(category)}
                            </th>
                        ))}
                        <th colSpan={2} className="text-center">Total</th>
                    </tr>
                    <tr>
                        {visibleCategories.map((category) => (
                            <React.Fragment key={category.id_product_categories}>
                                <th className="text-end">Quantity</th>
                                <th className="text-end">Amount</th>
                            </React.Fragment>
                        ))}
                        <th className="text-end">Quantity</th>
                        <th className="text-end">Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {reportData.months.map((row) => (
                        <tr key={row.month}>
                            <td>{row.month}</td>
                            {visibleCategories.map((category) => {
                                const categoryData = getCategoryValue(row.categories[String(category.id_product_categories)]);
                                return (
                                    <React.Fragment key={category.id_product_categories}>
                                        <td className="text-end">{formatReportQuantity(categoryData.quantity)}</td>
                                        <td className="text-end">{numberFormat(categoryData.amount)}</td>
                                    </React.Fragment>
                                );
                            })}
                            <td className="text-end">{formatReportQuantity(row.total.quantity)}</td>
                            <td className="text-end">{numberFormat(row.total.amount)}</td>
                        </tr>
                    ))}
                    <tr>
                        <td><strong>Grand Total</strong></td>
                        {visibleCategories.map((category) => {
                            const categoryData = getCategoryValue(reportData.grand_totals.categories[String(category.id_product_categories)]);
                            return (
                                <React.Fragment key={category.id_product_categories}>
                                    <td className="text-end">
                                        <strong>{formatReportQuantity(categoryData.quantity)}</strong>
                                    </td>
                                    <td className="text-end">
                                        <strong>{numberFormat(categoryData.amount)}</strong>
                                    </td>
                                </React.Fragment>
                            );
                        })}
                        <td className="text-end">
                            <strong>{formatReportQuantity(reportData.grand_totals.total.quantity)}</strong>
                        </td>
                        <td className="text-end">
                            <strong>{numberFormat(reportData.grand_totals.total.amount)}</strong>
                        </td>
                    </tr>
                </tbody>
            </Table>
        );
    };

    return (
        <>
            <Card className='mb-4'>
                <Card.Body>
                    <Card.Text>
                        <div className='row'>
                            <div className='col-md-3'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>Project<span className='required'></span></Form.Label>
                                    <Select
                                        value={project}
                                        onChange={handleProjectChange}
                                        options={projects}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        isClearable={true}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="fromDate">
                                    <Form.Label>From Month<span className='required'></span></Form.Label>
                                    <Form.Control
                                        type="month"
                                        name="fromDate"
                                        value={formData.fromDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="toDate">
                                    <Form.Label>To Month<span className='required'></span></Form.Label>
                                    <Form.Control
                                        type="month"
                                        name="toDate"
                                        value={formData.toDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="includeFreeItem">
                                    <Form.Label className='d-block'>&nbsp;</Form.Label>
                                    <div className="form-check mt-2">
                                        <Form.Check
                                            type="checkbox"
                                            id="includeFreeItem"
                                            name="includeFreeItem"
                                            label="Include Free Item"
                                            checked={formData.includeFreeItem}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <button className='btn btn-sm btn-primary p-3' onClick={handleViewReport} disabled={loading}>
                                        {loading && <span className="spinner-border spinner-border-sm"></span>}
                                        <span><i className='fas fa-eye'></i> View Report</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Card.Text>
                </Card.Body>
            </Card>

            <div className='row'>
                <div className='col-12'>
                    {reportData ? (
                        <>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <div>
                                    <strong>Period:</strong> {reportData.period_label}
                                </div>
                                <Form.Check
                                    type="checkbox"
                                    id="showAmount"
                                    name="showAmount"
                                    label="Show Amount"
                                    checked={formData.showAmount}
                                    onChange={handleChange}
                                />
                            </div>
                            {formData.showAmount ? renderQuantityAmountTable() : renderQuantityTable()}
                        </>
                    ) : (
                        <div>No data available</div>
                    )}
                </div>
            </div>
        </>
    );
};

export default GradeWiseSalesReport;
